import { useQuickActionFromUrl } from "components/GlobalSearch/useQuickActionFromUrl";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import EventNoteIcon from "@mui/icons-material/EventNote";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import HourglassBottomOutlinedIcon from "@mui/icons-material/HourglassBottomOutlined";
import PolicyOutlinedIcon from "@mui/icons-material/PolicyOutlined";
import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import EventAvailableOutlinedIcon from "@mui/icons-material/EventAvailableOutlined";
import moment from "moment";
import { useSelector } from "react-redux";
import { useFetch } from "services/hooks/useFetch";
import { checkEditorPermission } from "services/utils/HelperFunction";
import { showToast } from "services/utils/Status";
import { Loader } from "components/Loader";
import { PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { ApprovalDateRangeFilter } from "components/CustomComponents/ApprovalDateRangeFilter";
import { getThisMonthRange } from "components/CustomComponents/dateRangeDefaults";
import { CustomFilter } from "components/CustomComponents/CustomFilter";
import { useSavedViewSelector } from "components/CustomComponents/useSavedViewSelector";
import {
  HEADER_ACTION_BUTTON_WIDTH,
  paginationFooter,
  rowsPerPageOptions,
} from "services/constants/designTokens";
import { TableEmptyState } from "components/CustomComponents/EmptyState";
import { leavePalette, cardSx, statusMeta } from "../Leave/leaveHelpers";
import { RequestStatusCards } from "../RequestStatusCards";
import { CreateOTPreApproval } from "./OvertimePreApproval/CreateOTPreApproval";
import { ViewOTPreApproval } from "./OvertimePreApproval/ViewOTPreApproval";
import { ViewOTPostApproval } from "./OvertimeApproval/ViewOTPostApproval";

// Maps the sidebar Overtime workflow tab to the `ot_type` the unified endpoint
// (myworkspace-ot-approval/) expects. Pre-Approval = planned OT before work,
// Post-Approval = completed OT after work. Defaults to PreOT (the apply flow).
const OT_TYPE_BY_TAB = {
  "Pre-Approval": "PreOT",
  "Post-Approval": "PostOT",
};

// Seed the selector from the sidebar / URL context: Pre-Approval -> Planned,
// Post-Approval -> Completed, otherwise show everything.
const resolveInitialOtTab = (tab) => OT_TYPE_BY_TAB[tab] || "All";

const canonicalStatus = (status) => {
  const k = (status || "").trim().toLowerCase();
  if (k === "approved") return "Approved";
  if (k === "pending") return "Pending";
  if (k === "rejected") return "Rejected";
  if (k === "cancelled" || k === "canceled") return "Cancelled";
  return status;
};

const formatOTDateTime = (value) => {
  if (!value) return "-";
  const parsed = moment(
    value,
    ["DD-MM-YYYY, HH:mm", "DD-MM-YYYY, hh:mm A", "YYYY-MM-DD HH:mm:ss", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("DD MMM YYYY, hh:mm A") : value;
};

const toDateIso = (value) => {
  const parsed = moment(
    value,
    ["YYYY-MM-DD", "DD-MM-YYYY", "DD/MM/YYYY", "DD MMM YYYY", moment.ISO_8601],
    true,
  );
  return parsed.isValid() ? parsed.format("YYYY-MM-DD") : "";
};

const stripEmployeeIdFromName = (name, employeeId) => {
  const text = (name || "").trim();
  if (!text || !employeeId) return text;
  return text.replace(new RegExp(`\\s*[-|/]?\\s*${employeeId}\\s*$`, "i"), "").trim();
};

const resolveUserName = (userDetails) =>
  stripEmployeeIdFromName(
    userDetails?.first_name || userDetails?.user_name || userDetails?.username,
    userDetails?.employee_id,
  ) || "N/A";

// Typed history columns feed both the shared CustomFilter (search / column
// filters / sort) and the SavedViewSelector "Default View" dropdown, exactly
// like My Attendance.
const HEADERS = [
  { name: "Workflow", key: "workflow", type: "autocomplete", icon: <AccountTreeOutlinedIcon fontSize="small" /> },
  { name: "OT Date", key: "date", type: "date", icon: <CalendarMonthOutlinedIcon fontSize="small" /> },
  { name: "Applied On", key: "applied", type: "date", icon: <EventAvailableOutlinedIcon fontSize="small" /> },
  { name: "Requested Hours", key: "hours", type: "string", icon: <HourglassBottomOutlinedIcon fontSize="small" /> },
  { name: "Policy", key: "policy", type: "autocomplete", icon: <PolicyOutlinedIcon fontSize="small" /> },
  { name: "Status", key: "status", type: "autocomplete", icon: <CheckCircleOutlineIcon fontSize="small" /> },
];

const headCellSx = {
  color: leavePalette.text,
  fontWeight: 700,
  fontSize: 12,
  bgcolor: "#fff",
  borderBottom: `1px solid ${leavePalette.border}`,
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  borderBottom: `1px solid ${leavePalette.border}`,
  fontSize: 13,
  color: leavePalette.text,
};

// Redesigned My Zone > My Requests > Overtime APPLY screen (image 7). The two
// approval stages the backend keeps separate (Planned OT before work / Completed
// OT after) are merged into one list here, tagged by a Workflow chip, so the
// employee sees all their own overtime requests in one place. Applying still
// runs through the existing CreateOTPreApproval drawer; a row opens the existing
// ViewOTPreApproval drawer.
export const Overtime = ({ subModuleId, initialTab }) => {
  const { domain, userScreenAccess, topbarCompanyId, userDetails } = useSelector(
    (state) => state.tokenReducer,
  );

  // OT type context, seeded from the sidebar/url. The on-screen tab selector was
  // removed, so this stays fixed for the session and the list shows all types.
  const [otTypeTab] = useState(() => resolveInitialOtTab(initialTab));
  // ot_type sent when applying/editing: Completed -> PostOT, otherwise PreOT.
  const otType = otTypeTab === "PostOT" ? "PostOT" : "PreOT";

  const [statusFilter, setStatusFilter] = useState("");
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedStage, setSelectedStage] = useState(null);
  const [handleLoader, setHandleLoader] = useState(false);

  // CustomFilter output + pagination.
  const [tableRows, setTableRows] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[0]);
  // Default range is This Month (see getThisMonthRange).
  const [initialDateRange] = useState(() =>
    getThisMonthRange({ endAt: "monthEnd" }),
  );
  const [startDate, setStartDate] = useState(initialDateRange.startDate);
  const [endDate, setEndDate] = useState(initialDateRange.endDate);
  const [dateRangeLabel, setDateRangeLabel] = useState(initialDateRange.label);

  const checkAccess = checkEditorPermission(
    userScreenAccess?.module_details,
    "My Request",
    "Myzone Overtime",
  );

  // Unified OT endpoint: returns Pre-OT and Post-OT in one list, each row tagged
  // with `ot_type` ("PreOT" / "PostOT"). Replaces the previous two calls to
  // ot-pre-approval/ and ot-post-approval/.
  const {
    result: otData,
    setLoad: reCallOT,
    loading: otLoading,
  } = useFetch(
    `${domain}My_Zone/Apply/Over_time/myworkspace-ot-approval/?user_id=${userDetails?.id}&company_id=${topbarCompanyId}`,
  );

  useEffect(() => {
    if (topbarCompanyId) {
      reCallOT(true);
      setHandleLoader(false);
    }
  }, [reCallOT, topbarCompanyId]);

  // Split the unified list by ot_type into the same row shapes the table uses.
  const rows = useMemo(() => {
    const list = Array.isArray(otData) ? otData : [];
    const pre = list
      .filter((r) => r.ot_type === "PreOT")
      .map((r) => ({
        raw: r,
        stage: "pre",
        workflow: "Planned OT",
        date: r.ot_date || "-",
        applied: formatOTDateTime(
          r.applied_on || r.applied_date || r.created_at || r.created_date,
        ),
        hours: r.ot_duration || "-",
        policy: r.ot_consideration || "-",
        status: r.status,
        color: r.color_code,
      }));
    const post = list
      .filter((r) => r.ot_type === "PostOT")
      .map((r) => ({
        raw: r,
        stage: "post",
        workflow: "Completed OT",
        date: r.date || "-",
        applied: formatOTDateTime(
          r.applied_on || r.applied_date || r.created_at || r.created_date,
        ),
        hours: r.worked_hours != null ? `${r.worked_hours} Hrs` : "-",
        policy: r.payout_policy || "-",
        status: r.status,
        color: r.color_code,
      }));
    return [...pre, ...post];
  }, [otData]);

  const dateFilteredRows = useMemo(
    () =>
      rows.filter((row) => {
        const rowDate = toDateIso(row.date);
        if (!rowDate) return true;
        return rowDate >= startDate && rowDate <= endDate;
      }),
    [endDate, rows, startDate],
  );

  // Rows narrowed by the OT-type tab (All / Planned / Completed). Everything
  // downstream (status tiles, counts, table) is scoped to the selected type.
  const typeFilteredRows = useMemo(
    () =>
      otTypeTab === "All"
        ? dateFilteredRows
        : dateFilteredRows.filter((r) => r.raw?.ot_type === otTypeTab),
    [dateFilteredRows, otTypeTab],
  );

  const counts = useMemo(() => {
    const c = { Pending: 0, Approved: 0, Rejected: 0, Cancelled: 0 };
    typeFilteredRows.forEach((r) => {
      const s = canonicalStatus(r.status);
      if (c[s] !== undefined) c[s] += 1;
    });
    return c;
  }, [typeFilteredRows]);

  // Rows fed to CustomFilter: pre-filtered by the clickable status tiles. The
  // shared filter/search/sort then reads the flat row[key] columns directly.
  const filterInputRows = useMemo(
    () =>
      typeFilteredRows.filter(
        (r) => !statusFilter || canonicalStatus(r.status) === statusFilter,
      ),
    [typeFilteredRows, statusFilter],
  );

  const handleFilteredDataChange = useCallback(
    (filtered = []) => {
      setTableRows(filtered);
      setPage((prev) => (prev * rowsPerPage >= filtered.length ? 0 : prev));
    },
    [rowsPerPage],
  );

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleDateRangeChange = useCallback((rangeStart, rangeEnd, rangeLabel) => {
    setStartDate(moment(rangeStart).format("YYYY-MM-DD"));
    setEndDate(moment(rangeEnd).format("YYYY-MM-DD"));
    setDateRangeLabel(rangeLabel || "Custom Range");
    setPage(0);
  }, []);

  const { visibleHeaders, titleOverride } = useSavedViewSelector({
    domain,
    userId: userDetails?.id,
    subModuleId,
    storageKey: "myzone_overtime_saved_views",
    fullHeaders: HEADERS,
    screenAccess: checkAccess,
    onViewChange: () => setPage(0),
  });

  const reportMetadata = useMemo(
    () => ({
      "User Name": resolveUserName(userDetails),
      Type: "Overtime",
      "Report Generated On": moment().format("DD/MM/YYYY HH:mm:ss"),
    }),
    [userDetails],
  );

  const paginatedRows = useMemo(
    () => tableRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [tableRows, page, rowsPerPage],
  );

  const openApply = () => {
    if (checkAccess) setIsCreateOpen(true);
    else
      showToast("error", "Access denied: You are not authorized to apply for overtime");
  };

  // Search quick action "Apply Overtime" opens the New Request panel.
  useQuickActionFromUrl(
    { "apply-overtime": checkAccess ? openApply : null },
    Boolean(userScreenAccess?.module_details),
  );

  const openView = (row) => {
    setSelectedItem(row.raw);
    setSelectedStage(row.stage);
    setIsViewOpen(true);
  };

  const renderCell = (row, head) => {
    switch (head.key) {
      case "workflow": {
        const planned = row.stage === "pre";
        const Wf = planned ? EventNoteIcon : DoneAllIcon;
        const wfColor = planned ? leavePalette.blue : leavePalette.green;
        return (
          <Chip
            size="small"
            icon={<Wf sx={{ fontSize: 16, color: `${wfColor} !important` }} />}
            label={row.workflow}
            sx={{
              bgcolor: `${wfColor}14`,
              color: wfColor,
              fontWeight: 600,
              borderRadius: 1.5,
            }}
          />
        );
      }
      case "status": {
        const color = statusMeta(row.status, row.color);
        return (
          <Chip
            size="small"
            label={row.status || "N/A"}
            sx={{
              bgcolor: `${color}1A`,
              color,
              fontWeight: 700,
              borderRadius: 1.5,
            }}
          />
        );
      }
      case "date":
      case "applied":
        return (
          <Typography variant="body2" sx={{ color: leavePalette.text }}>
            {row[head.key]}
          </Typography>
        );
      case "policy":
        return (
          <Typography variant="body2" sx={{ color: leavePalette.subText }}>
            {row.policy}
          </Typography>
        );
      default:
        return <Typography variant="body2">{row[head.key] ?? "-"}</Typography>;
    }
  };

  const OvertimeMobileCard = ({ row }) => {
    const statusHeader = visibleHeaders.find((head) => head.key === "status");
    const detailHeaders = visibleHeaders.filter((head) => head.key !== "status");

    return (
      <Box
        onClick={() => openView(row)}
        sx={{
          p: 1.5,
          border: `1px solid ${leavePalette.border}`,
          borderRadius: 2,
          backgroundColor: "#fff",
          cursor: "pointer",
        }}
      >
        <Stack spacing={1.25}>
          <Stack direction="row" spacing={1} justifyContent="space-between" alignItems="center">
            <Typography
              sx={{
                fontSize: 14,
                fontWeight: 700,
                color: leavePalette.text,
                minWidth: 0,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {row.workflow}
            </Typography>
            {statusHeader ? renderCell(row, statusHeader) : null}
          </Stack>
          {detailHeaders.map((head) => (
            <Stack
              key={head.key}
              direction="row"
              spacing={1}
              justifyContent="space-between"
              sx={{ minWidth: 0 }}
            >
              <Typography sx={{ fontSize: 12, color: leavePalette.subText }}>
                {head.name}
              </Typography>
              <Box
                sx={{
                  fontSize: 12.5,
                  color: leavePalette.text,
                  fontWeight: 600,
                  maxWidth: "58%",
                  textAlign: "right",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {renderCell(row, head)}
              </Box>
            </Stack>
          ))}
        </Stack>
      </Box>
    );
  };

  if (!handleLoader && otLoading) return <Loader />;

  return (
    <React.Fragment>
      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        {/* Header */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={1.5}
          sx={{ mb: 2.5 }}
        >
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ color: leavePalette.text }}>
              Overtime
            </Typography>
            <Typography variant="body2" sx={{ color: leavePalette.subText }}>
              Apply for overtime and track your overtime requests
            </Typography>
          </Box>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.25}
            alignItems={{ xs: "stretch", sm: "center" }}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <ApprovalDateRangeFilter
              startDate={startDate}
              endDate={endDate}
              dateRangeLabel={dateRangeLabel}
              onChange={handleDateRangeChange}
              includeCurrentDate
              showToday
            />
            {checkAccess ? (
              <PrimaryButton
                startIcon={<AddIcon sx={{ fontSize: 18 }} />}
                sx={{
                  minWidth: { xs: 0, sm: HEADER_ACTION_BUTTON_WIDTH },
                  width: { xs: "100%", sm: "auto" },
                }}
                onClick={openApply}
              >
                New Request
              </PrimaryButton>
            ) : null}
          </Stack>
        </Stack>

        {/* Status cards (quick filters) - same shared cards as Team Workspace > Approvals */}
        <RequestStatusCards
          counts={counts}
          total={typeFilteredRows.length}
          activeStatus={statusFilter}
          onSelect={(value) => {
            setStatusFilter(value);
            setPage(0);
          }}
        />

        {/* Table card */}
        <Box sx={{ ...cardSx, p: 2.25 }}>
          {/* My Attendance-style toolbar: Default View · Search · Download · Sort · Filters */}
          <CustomFilter
            title="Overtime"
            titleOverride={titleOverride}
            filterHeaders={visibleHeaders}
            data={filterInputRows}
            filteredData={tableRows}
            onFilteredDataChange={handleFilteredDataChange}
            screenAccess={checkAccess}
            showSaveButton={true}
            hideCount={true}
            downloadFormats={["excel", "pdf", "csv"]}
            includeMetadata={true}
            metadata={reportMetadata}
            labeledToolbar={true}
            searchPlaceholder="Search by policy, date or workflow..."
          />

          <Box sx={{ display: { xs: "block", sm: "none" } }}>
            {paginatedRows.length === 0 ? (
              <Box
                sx={{
                  py: 4,
                  textAlign: "center",
                  color: leavePalette.subText,
                  border: `1px solid ${leavePalette.border}`,
                  borderRadius: 2,
                  backgroundColor: "#fff",
                }}
              >
                No overtime requests found.
              </Box>
            ) : (
              <Stack spacing={1.25}>
                {paginatedRows.map((row, index) => (
                  <OvertimeMobileCard
                    key={`${row.stage}-${row.raw?.id ?? index}`}
                    row={row}
                  />
                ))}
              </Stack>
            )}
          </Box>

          <TableContainer sx={{ overflowX: "auto", display: { xs: "none", sm: "block" } }}>
            <Table size="small" sx={{ minWidth: 720 }}>
              <TableHead>
                <TableRow>
                  {visibleHeaders.map((head) => (
                    <TableCell key={head.key} sx={headCellSx}>
                      {head.name}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRows.length === 0 ? (
                  <TableEmptyState
                    colSpan={visibleHeaders.length || 1}
                    dataLength={paginatedRows}
                    title="No Overtime Records Found"
                    subtitle="There's nothing to show here yet."
                  />
                ) : (
                  paginatedRows.map((r, i) => {
                    return (
                      <TableRow
                        key={`${r.stage}-${r.raw?.id ?? i}`}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => openView(r)}
                      >
                        {visibleHeaders.map((head) => (
                          <TableCell key={head.key} sx={bodyCellSx}>
                            {renderCell(r, head)}
                          </TableCell>
                        ))}
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ mt: 1.5, pt: 1.5, borderTop: `1px solid ${leavePalette.border}` }}>
            {paginationFooter({
              page,
              rowsPerPage,
              total: tableRows.length,
              itemLabel: "records",
              onPageChange: setPage,
              onRowsPerPageChange: handleChangeRowsPerPage,
              rowsPerPageOptions,
            })}
          </Box>
        </Box>
      </Box>

      {/* Side panels */}
      {isCreateOpen ? (
        <CreateOTPreApproval
          open={isCreateOpen}
          onClose={() => setIsCreateOpen(false)}
          reCallApi={reCallOT}
          setHandleLoader={setHandleLoader}
        />
      ) : null}
      {isViewOpen && selectedStage === "post" ? (
        <ViewOTPostApproval
          open={isViewOpen}
          selectedData={selectedItem}
          onClose={() => setIsViewOpen(false)}
          onUpdated={() => reCallOT(true)}
        />
      ) : null}
      {isViewOpen && selectedStage !== "post" ? (
        <ViewOTPreApproval
          open={isViewOpen}
          onClose={() => setIsViewOpen(false)}
          editOTPreAppDetails={selectedItem}
          reCallApi={reCallOT}
          setGetId={() => {}}
          setHandleLoader={setHandleLoader}
          screenAccess={checkAccess}
          otType={selectedItem?.ot_type || otType}
        />
      ) : null}
    </React.Fragment>
  );
};
